import React from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Phone } from 'lucide-react';

const VentilationPage = () => {
  return (
    <div className="VentilationPage bg-white">
      {/* Hero Section */}
      <section
        className="relative bg-cover bg-center py-24"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1708705261211-b2a244a811dc)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#2D9A68]/95 to-[#2F5BA0]/85"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-3xl text-white">
            <p className="text-lg mb-3 font-semibold opacity-90">🌀 Qualité de l'air</p>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Ventilation VMC Double Flux</h1>
            <p className="text-xl md:text-2xl mb-6">Air pur et économies d'énergie - Récupération de chaleur</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="bg-[#2F5BA0] text-white px-8 py-4 rounded-lg font-bold hover:bg-[#1e3a66] transition-all inline-flex items-center justify-center gap-2"
              >
                Nous contacter
              </Link>
              <a
                href="tel:0188617508"
                className="bg-white text-[#2D9A68] px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-all inline-flex items-center justify-center gap-2"
              >
                <Phone size={20} />
                01 88 61 75 08
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Aides */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="bg-gradient-to-r from-[#2D9A68] to-[#2F5BA0] text-white rounded-2xl p-8 text-center shadow-lg">
              <p className="text-lg mb-2 opacity-90">💰 Aides disponibles</p>
              <p className="text-3xl md:text-4xl font-bold">Jusqu'à 2 700€ (MaPrimeRénov' 2 500€ + CEE 700€)</p>
            </div>
          </div>
        </div>
      </section>

      {/* Images */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {[
                'https://www.humidite.pro/wp-content/uploads/VMC-double-flux-%C2%A9-VectorMine-Shutterstock.png',
                'https://customer-assets.emergentagent.com/job_3ccb3377-9827-446a-8cff-ddaef1849a1d/artifacts/iyxqj7x0_nano-banana-2025-10-16T16-42-38.png',
                'https://www.reseau-proeco-energies.fr/img/realisations/1960-67f005815750c.jpeg',
              ].map((img, index) => (
                <div key={index} className="rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all bg-white flex items-center justify-center" style={{minHeight: '300px'}}>
                  <img 
                    src={img} 
                    alt={`VMC Double Flux - ${index + 1}`}
                    className="w-full h-auto max-h-96 object-contain p-4"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Qu'est-ce que */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Qu'est-ce qu'une VMC double flux ?</h3>
            <p className="mb-4 text-gray-700 leading-relaxed text-lg">
              La VMC (Ventilation Mécanique Contrôlée) double flux est un système de ventilation intelligent qui renouvelle l'air de votre logement tout en récupérant la chaleur de l'air vicié extrait. Contrairement à une VMC simple flux qui évacue l'air chaud vers l'extérieur, la double flux préchauffe l'air neuf entrant grâce à un échangeur thermique, permettant des économies de chauffage de 15 à 20%.
            </p>
            <p className="mb-4 text-gray-700 leading-relaxed text-lg">
              Indispensable dans les logements bien isolés (BBC, RT 2012, RE 2020), la VMC double flux garantit une qualité d'air optimale en évacuant l'humidité, les polluants et le CO₂, tout en filtrant l'air entrant (pollens, particules fines).
            </p>
          </div>
        </div>
      </section>

      {/* Comment ça fonctionne */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Comment fonctionne une VMC double flux ?</h3>
            <p className="mb-4 text-gray-700 leading-relaxed text-lg">
              Le principe repose sur deux réseaux de gaines indépendants :
            </p>
            <ol className="list-decimal list-inside space-y-3 text-gray-700 mb-6 text-lg ml-4">
              <li><strong>Extraction :</strong> L'air vicié et chaud est aspiré dans les pièces humides (cuisine, salle de bains, WC)</li>
              <li><strong>Échange thermique :</strong> Cet air traverse un échangeur qui transfère sa chaleur à l'air neuf entrant (rendement 85-95%)</li>
              <li><strong>Insufflation :</strong> L'air neuf préchauffé et filtré est soufflé dans les pièces de vie (salon, chambres)</li>
              <li><strong>Filtration :</strong> L'air entrant est filtré (pollens, particules, pollution)</li>
            </ol>
            <p className="mb-4 text-gray-700 leading-relaxed text-lg">
              L'installation nécessite un faux plafond ou des combles accessibles pour faire passer les gaines. Un caisson de ventilation central (installé dans les combles ou un local technique) contient les ventilateurs et l'échangeur thermique.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 my-6 rounded-lg">
              <p className="text-blue-900 font-semibold mb-2 text-lg">💡 Santé et confort</p>
              <p className="text-blue-800 text-lg">La VMC double flux élimine 95% des polluants intérieurs et réduit drastiquement les risques d'allergies, d'asthme et de problèmes respiratoires. L'air est renouvelé en permanence sans courants d'air froid !</p>
            </div>
          </div>
        </div>
      </section>

      {/* Avantages et Inconvénients */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Avantages & Inconvénients</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">✅ Avantages</h3>
                <div className="space-y-4">
                  {[
                    '🌀 Air pur renouvelé en permanence',
                    '💰 15-20% d\'économies sur chauffage',
                    '🔥 Aides jusqu\'à 2 700€',
                    '🤧 Réduit allergies',
                    '💧 Élimine humidité et moisissures',
                    '🌡️ Récupération 90% chaleur',
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <Check className="text-[#2D9A68] mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">⚠️ Inconvénients</h3>
                <div className="space-y-4">
                  {[
                    '🏗️ Nécessite combles accessibles',
                    '🔧 Installation gaines complexe',
                    '🔄 Entretien filtres 2x/an',
                    '💶 Coût initial élevé',
                    '⚡ Consommation électrique continue',
                    '🏠 Idéal en construction/rénovation totale',
                  ].map((inconvenient, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <span className="text-red-500 mt-1 flex-shrink-0">✕</span>
                      <span className="text-gray-700">{inconvenient}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pour quels logements */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Pour quel type de logement ?</h3>
            <ul className="list-disc list-inside space-y-3 text-gray-700 mb-4 text-lg">
              <li><strong>Maisons neuves ou rénovation complète</strong> (nécessite passage de gaines)</li>
              <li><strong>Logements BBC, RT 2012, RE 2020 :</strong> Quasi obligatoire pour étanchéité</li>
              <li><strong>Nécessite combles accessibles ou faux plafonds</strong> pour passage des gaines</li>
              <li><strong>Idéal si rénovation complète :</strong> Profitez-en pour installer la VMC DF</li>
              <li>Surface : Tous logements de 60 à 250 m²</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Caractéristiques techniques */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Caractéristiques Techniques</h2>
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <ul className="space-y-4">
                {[
                  'Débit : 120 à 300 m³/h selon surface',
                  'Rendement échangeur : 85 à 95%',
                  'Filtres : F7 à F9 (haute filtration)',
                  'Niveau sonore : < 30 dB (ultra-silencieux)',
                  'Consommation électrique : 300-500 W',
                  'Gaines : Ø 80-125 mm semi-rigides',
                  'Maintenance : Changement filtres 2 fois/an',
                  'Garantie : 2 ans (moteurs garantis 5 ans)',
                ].map((spec, index) => (
                  <li key={index} className="flex items-start gap-3 text-lg">
                    <Check className="text-[#2F5BA0] mt-1 flex-shrink-0" size={20} />
                    <span className="text-gray-700">{spec}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 bg-gradient-to-r from-[#2D9A68] to-[#2F5BA0] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Intéressé par une VMC double flux ?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Nos experts vous conseillent sur l'installation et le dimensionnement adapté
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/test-eligibilite"
              className="bg-[#E6A835] text-white px-8 py-4 rounded-lg font-bold hover:bg-[#d49520] transition-all inline-flex items-center justify-center gap-2"
            >
              Testez votre éligibilité <ArrowRight />
            </Link>
            <Link
              to="/contact"
              className="bg-white text-[#2D9A68] px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-all inline-flex items-center justify-center gap-2"
            >
              Demander un devis gratuit
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VentilationPage;
